import React, { useMemo, useRef, useState } from "react";
import { Dialog, useRunner, initials } from "./parts";
import { roomsApi } from "./useRoom";
import { trimDraft, cutRange, restoreCut, keptText } from "./roomModel.mjs";
import { shortDate } from "../record/format.mjs";

/*
 * AN EMAIL ON THE ROOM, TRIMMED (EmailTrim board). The quoted earlier messages,
 * the signature and any forwarded headers are cut automatically and listed with
 * Restore; anything else he selects and cuts by hand, and on the room a hand cut
 * reads as one quiet "…". "As <company> sees it" is the room's text exactly. The
 * email in Gmail is never touched: what is stored is the kept text.
 *
 * Props: companyId, companyName, email { activity_id, body, subject, at, from:
 *        { name, us }, to: [name] } for an add, or item for an edit; onClose
 */
export default function EmailTrimDialog({ companyId, companyName, email = null, item = null, onClose }) {
  const editing = !!item;
  const start = useMemo(() => {
    if (editing) {
      const c = item.content || {};
      return { segments: c.segments || [{ t: c.text || "", cut: false }], auto: c.auto_cuts || [], restored: (c.restored || []).map((r) => r.kind) };
    }
    const d = trimDraft(email.body);
    return { segments: d.segments, auto: d.auto_cuts, restored: [] };
  }, [editing, item, email]);
  const [segments, setSegments] = useState(start.segments);
  const [restoredKinds, setRestoredKinds] = useState(start.restored);
  const [tab, setTab] = useState("trim");
  const [pending, setPending] = useState(null); // { a, b, x, y }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = useRunner(setBusy, setErr);
  const boxRef = useRef(null);

  const head = editing ? item.content : email;
  const from = (head.from && head.from.name) || "";
  const us = !!(head.from && head.from.us);
  const auto = start.auto;
  const restored = auto.filter((a) => restoredKinds.includes(a.kind)).map((a) => ({ kind: a.kind, text: a.text }));
  const shown = keptText(segments, restored);
  const handCuts = segments.filter((s) => s.cut).length;

  // A selection inside the body → where it starts and ends, in segment terms.
  const posOf = (node, offset) => {
    const el = node && (node.nodeType === 1 ? node : node.parentElement);
    const span = el && el.closest("[data-seg]");
    if (!span || !boxRef.current || !boxRef.current.contains(span)) return null;
    const i = Number(span.getAttribute("data-seg"));
    if (node.nodeType !== 3) return { i, off: offset === 0 ? 0 : segments[i].t.length };
    return { i, off: offset };
  };
  const onSelect = () => {
    const sel = typeof window !== "undefined" ? window.getSelection() : null;
    if (!sel || sel.isCollapsed || !sel.rangeCount) return setPending(null);
    const a = posOf(sel.anchorNode, sel.anchorOffset);
    const b = posOf(sel.focusNode, sel.focusOffset);
    if (!a || !b) return setPending(null);
    if (segments[a.i].cut || segments[b.i].cut) return setPending(null);
    const rect = sel.getRangeAt(0).getBoundingClientRect();
    setPending({ a, b, x: Math.max(8, rect.left), y: Math.max(8, rect.top - 36) });
  };
  const doCut = () => {
    if (!pending) return;
    setSegments((s) => cutRange(s, pending.a, pending.b));
    setPending(null);
    if (window.getSelection) window.getSelection().removeAllRanges();
  };

  const save = () =>
    run(async () => {
      const body = { segments, restored, auto_cuts: auto };
      if (editing) await roomsApi.editItem(companyId, item.id, body);
      else await roomsApi.add(companyId, { section: "conversation", activity_id: email.activity_id, ...body });
      onClose(true);
    });

  const KINDS = [
    { kind: "quoted", label: "Quoted earlier messages" },
    { kind: "signature", label: "Signature block" },
    { kind: "headers", label: "Forwarded headers" },
  ];

  return (
    <Dialog
      title={editing ? "Edit email on the room" : `Add email to ${companyName}’s room`}
      sub="Goes into Our conversation · newest first"
      onClose={() => onClose(false)}
      foot={
        <>
          <span className="rm-sub">The email in Gmail is not changed</span>
          {err && <span className="rm-err">{err}</span>}
          <button type="button" className="rm-btn" onClick={() => onClose(false)}>Cancel</button>
          <button type="button" className="rm-btn rm-btn--primary" disabled={busy || !shown.trim()} onClick={save}>
            {busy ? "Saving…" : editing ? "Save" : "Add to room"}
          </button>
        </>
      }
    >
      <div className="rm-tabs">
        <button type="button" className="rm-tab" aria-pressed={tab === "trim"} onClick={() => setTab("trim")}>Trim</button>
        <button type="button" className="rm-tab" aria-pressed={tab === "preview"} onClick={() => { setTab("preview"); setPending(null); }}>As {companyName} sees it</button>
        {tab === "trim" && <span className="rm-tabs__hint">Select text to cut it</span>}
      </div>

      <div className="rm-from">
        <span aria-hidden="true" className={`rm-av${us ? " rm-av--us" : ""}`}>{initials(from)}</span>
        <div className="rm-from__b">
          <div className="rm-from__line">
            <span>
              <b>{from}</b>
              {head.to && head.to.length ? <span className="rm-sub"> to {head.to.join(", ")}</span> : null}
            </span>
            <span className="rm-sub">{head.at ? shortDate(head.at) : ""}</span>
          </div>
          <div className="rm-from__subj">{head.subject || "(no subject)"}</div>
        </div>
      </div>

      {tab === "trim" ? (
        <div ref={boxRef} className="rm-mail" onMouseUp={onSelect} onKeyUp={onSelect}>
          {segments.map((s, i) =>
            s.cut ? (
              <span key={i}>
                <span className="rm-cut" data-seg={i}>{s.t}</span>
                <button type="button" className="rm-restore" onClick={() => setSegments((x) => restoreCut(x, i))}>Restore</button>
              </span>
            ) : (
              <span key={i} data-seg={i}>{s.t}</span>
            )
          )}
        </div>
      ) : (
        <div className="rm-mail rm-mail--preview">{shown || "Nothing left: restore something, or cancel."}</div>
      )}
      {pending && tab === "trim" && (
        <button type="button" className="rm-cutbtn" style={{ left: pending.x, top: pending.y }} onMouseDown={(e) => e.preventDefault()} onClick={doCut}>
          ✂ Cut
        </button>
      )}

      <div className="rm-hint">
        On the room, a hand cut shows as a quiet “…”. Automatic cuts leave no mark.
        {handCuts > 0 ? ` ${handCuts} cut by hand.` : ""}
      </div>

      <details className="rm-left" open>
        <summary>
          Cut automatically <span>· {auto.length} {auto.length === 1 ? "part" : "parts"}</span>
        </summary>
        <div className="rm-auto">
          {KINDS.map((k) => {
            const a = auto.find((x) => x.kind === k.kind);
            if (!a) {
              return (
                <div key={k.kind} className="rm-auto__row is-none">
                  <b>{k.label}</b>
                  <span>None in this email</span>
                </div>
              );
            }
            const back = restoredKinds.includes(k.kind);
            return (
              <div key={k.kind} className="rm-auto__row">
                <b>{k.label}</b>
                <span title={a.text}>{back ? "Restored: shown at the end of the email" : a.summary || a.text.slice(0, 80)}</span>
                <button
                  type="button"
                  className="rm-link rm-link--strong"
                  onClick={() => setRestoredKinds((x) => (back ? x.filter((y) => y !== k.kind) : [...x, k.kind]))}
                >
                  {back ? "Cut again" : "Restore"}
                </button>
              </div>
            );
          })}
        </div>
      </details>
    </Dialog>
  );
}
